import apiService from './apiService';

/**
 * Service for handling Composio MCP integration
 * Manages app connections, available apps, and authentication
 */
class ComposioService {
  /**
   * Get all available Composio apps with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20)
   * @param {string} params.search - Search query
   * @returns {Promise<Object>} List of available apps with pagination metadata
   */
  async getAvailableApps(params = {}) {
    try {
      const queryParams = {
        page: params.page || 1,
        page_size: params.page_size || 20,
        ...(params.search && { search: params.search }),
      };

      const response = await apiService.get('/composio/apps', queryParams);
      return response;
    } catch (error) {
      console.error('Error fetching available Composio apps:', error);
      throw error;
    }
  }

  /**
   * Get all connected apps for the current user
   * @returns {Promise<Object>} List of connected apps with integration details
   */
  async getConnectedApps() {
    try {
      const response = await apiService.get('/composio/connected');
      return response;
    } catch (error) {
      console.error('Error fetching connected apps:', error);
      throw error;
    }
  }

  /**
   * Fetch dynamic auth field metadata for a toolkit. Drives the connection form.
   * @param {string} toolkitSlug - Toolkit slug (app.key)
   * @returns {Promise<{toolkit_slug: string, toolkit_name: string, no_auth: boolean, auth_schemes: Array}>}
   */
  async getAuthFields(toolkitSlug) {
    try {
      const response = await apiService.get(`/composio/apps/${toolkitSlug}/auth-fields`);
      return response;
    } catch (error) {
      console.error(`Error fetching auth fields for toolkit ${toolkitSlug}:`, error);
      throw error;
    }
  }

  /**
   * Initiate connection to a Composio app
   * @param {string} appId - The ID of the app to connect
   * @param {Object} connectData - Connection configuration data
   * @param {string} connectData.redirectUrl - Optional redirect URL after OAuth
   * @param {string} connectData.authMode - Authentication mode (OAUTH2, API_KEY, BEARER_TOKEN, BASIC_AUTH, NO_AUTH, DCR_OAUTH)
   * @param {Object} connectData.authConfig - Flat dict of auth fields keyed by the names from /auth-fields
   * @param {string} connectData.toolkit_slug - Toolkit slug from app.key field
   * @param {string} connectData.toolkit_name - Toolkit display name from app.name field
   * @returns {Promise<Object>} Authentication URL and integration ID
   */
  async connectApp(appId, connectData = {}) {
    try {
      // Set default redirect URL to frontend OAuth callback page
      const frontendCallbackUrl = `${window.location.origin}/oauth/callback`;
      const defaultRedirectUrl = connectData.redirectUrl || frontendCallbackUrl;

      const payload = {
        redirect_url: defaultRedirectUrl,
        toolkit_slug: connectData.toolkit_slug,
        toolkit_name: connectData.toolkit_name,
        ...(connectData.authMode && { auth_mode: connectData.authMode }),
      };

      // Only attach auth_config when the user actually provided fields.
      // Managed OAuth and NO_AUTH connections must NOT send auth_config.
      if (connectData.authConfig && Object.keys(connectData.authConfig).length > 0) {
        payload.auth_config = connectData.authConfig;
      }

      // Log field names only: auth_config can hold a client secret or API key.
      console.log('ComposioService - Sending payload:', {
        ...payload,
        ...(payload.auth_config && { auth_config: Object.keys(payload.auth_config) }),
      });
      const response = await apiService.post(`/composio/connect/${appId}`, payload);
      return response;
    } catch (error) {
      console.error(`Error connecting to app ${appId}:`, error);
      throw error;
    }
  }

  /**
   * Disconnect a connected app
   * @param {string} appName - The name of the app to disconnect
   * @param {string} entityId - Optional entity ID for the connection
   * @returns {Promise<Object>} Success status
   */
  async disconnectApp(appName, entityId = null) {
    try {
      const body = {
        app_name: appName,
        ...(entityId && { entity_id: entityId })
      };
      const response = await apiService.post('/composio/disconnect', body);
      return response;
    } catch (error) {
      console.error(`Error disconnecting app ${appName}:`, error);
      throw error;
    }
  }

  /**
   * Get available actions for a specific app
   * @param {string} appId - The ID of the app
   * @returns {Promise<Object>} List of available actions
   */
  async getAppActions(appId) {
    try {
      const response = await apiService.get(`/composio/apps/${appId}/actions`);
      return response;
    } catch (error) {
      console.error(`Error fetching actions for app ${appId}:`, error);
      throw error;
    }
  }


  /**
   * Check connection status for a specific app
   * @param {string} appId - The ID of the app to check
   * @returns {Promise<Object>} Connection status information
   */
  async getConnectionStatus(appId) {
    try {
      const response = await apiService.get(`/composio/status/${appId}`);
      return response;
    } catch (error) {
      console.error(`Error checking connection status for app ${appId}:`, error);
      throw error;
    }
  }

  /**
   * Handle OAuth redirect callback
   * Forwards the return page's query string unchanged. `connectedAccountId` and
   * `appName` may be missing; the backend recovers them from `state`.
   * @param {Object} queryParams - Every query parameter of the return URL (state, status, code, ...)
   * @returns {Promise<Object>} Connection result
   */
  async handleOAuthCallback(queryParams = {}) {
    try {
      // apiService.get already wraps its second argument as axios `params`.
      const response = await apiService.get('/composio/callback', queryParams);
      return response;
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      throw error;
    }
  }

  // --- Composio triggers & project webhooks (V3) ---

  async getInboundWebhookUrl() {
    try {
      return await apiService.get('/composio/webhooks/inbound-url');
    } catch (error) {
      console.error('Error fetching Composio inbound webhook URL:', error);
      throw error;
    }
  }

  async getSetupStatus() {
    try {
      return await apiService.get('/composio/webhooks/setup-status');
    } catch (error) {
      console.error('Error fetching Composio setup status:', error);
      throw error;
    }
  }

  async getWebhookSubscription() {
    try {
      return await apiService.get('/composio/webhooks/subscription');
    } catch (error) {
      console.error('Error fetching Composio webhook subscription:', error);
      throw error;
    }
  }

  async subscribeWebhook(body = {}) {
    try {
      return await apiService.post('/composio/webhooks/subscribe', body);
    } catch (error) {
      console.error('Error subscribing Composio webhook:', error);
      throw error;
    }
  }

  /**
   * @param {string} toolkitSlug - e.g. github, slack
   */
  async listTriggerTypes(toolkitSlug) {
    try {
      // Composio toolkit slugs are lowercase with no spaces (e.g. "google drive" → "googledrive").
      const slug = toolkitSlug ? String(toolkitSlug).toLowerCase().replace(/\s+/g, '') : '';
      const params = slug ? { toolkit_slug: slug } : {};
      return await apiService.get('/composio/triggers/types', params);
    } catch (error) {
      console.error(`Error listing trigger types for ${toolkitSlug}:`, error);
      throw error;
    }
  }

  async getTriggerType(slug) {
    try {
      return await apiService.get(`/composio/triggers/types/${encodeURIComponent(slug)}`);
    } catch (error) {
      console.error(`Error fetching trigger type ${slug}:`, error);
      throw error;
    }
  }

  async listTriggerInstances() {
    try {
      return await apiService.get('/composio/triggers/instances');
    } catch (error) {
      console.error('Error listing Composio trigger instances:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const composioService = new ComposioService();
export default composioService;
